/**
 * Edit a saved view — Step 42.
 *
 * Kai: *"Views should also be able to made global, or shared with a group, or
 * groups, or just personal."* All four, and the plural one is the case a real
 * desk hits constantly: "All Broadcast - Open" belongs to Broadcast
 * Engineering AND Broadcast Systems, and with a single group the only options
 * were duplicate it or widen it to the whole organisation.
 *
 * Editing the NAME and SHARING only — never the filter. The filter is
 * whatever the queue is currently showing, saved from there, so that the
 * thing you looked at is the thing you saved. A second place to edit
 * conditions would be a fourth copy of the condition builder, which is
 * exactly what this step exists to stop.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '../../components/ui/modal';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select } from '../../components/ui/select';
import { apiSend } from '../../lib/api';
import { qk } from '../../lib/queryClient';
import { ticketsApi } from '../../features/tickets/api';
import { useDirty } from '../../lib/dirty';
import type { SavedView } from '../../features/tickets/views';

/** Offered sizes. Bounded by the API's `min(10).max(200)` — a view asking for
 *  5,000 rows is a slow queue and a facet query that times out. */
const PAGE_SIZES = [10, 25, 50, 100, 200] as const;

export function ViewEditModal({ view, onClose }: { view: SavedView | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [scope, setScope] = useState<'private' | 'group' | 'org'>('private');
  const [groupIds, setGroupIds] = useState<string[]>([]);
  /** Rows per page, as a STRING — `''` means "use the default". Kept as the
   *  raw select value so "Default" is a real choice rather than a magic
   *  number the user has to know is the default. */
  const [pageSize, setPageSize] = useState('');
  const [error, setError] = useState<string | null>(null);

  const groups = useQuery({ queryKey: qk.orgGroups, queryFn: ticketsApi.groups, staleTime: 5 * 60_000 });

  useEffect(() => {
    if (!view) return;
    setName(view.name);
    setScope(view.scope);
    setGroupIds(view.groupIds ?? []);
    setPageSize(view.pageSize ? String(view.pageSize) : '');
    setError(null);
  }, [view]);

  const dirty =
    !!view &&
    (name !== view.name ||
      scope !== view.scope ||
      pageSize !== (view.pageSize ? String(view.pageSize) : '') ||
      JSON.stringify([...groupIds].sort()) !== JSON.stringify([...(view.groupIds ?? [])].sort()));
  const clearDirty = useDirty(!!view && dirty);

  const save = useMutation({
    mutationFn: () =>
      apiSend(`/api/views/${view!.id}`, 'PATCH', {
        name,
        scope,
        // Sent even when empty, so switching away from `group` unshares it.
        groupIds: scope === 'group' ? groupIds : [],
        // `null`, not omitted: the PATCH treats `undefined` as "leave alone",
        // so clearing back to Default has to say so explicitly.
        pageSize: pageSize ? Number(pageSize) : null,
      }),
    onSuccess: (r) => {
      if (!r.ok) return setError(r.error ?? 'Could not save');
      clearDirty();
      void qc.invalidateQueries({ queryKey: qk.views });
      onClose();
    },
  });

  const close = () => {
    clearDirty();
    onClose();
  };

  return (
    <Modal open={!!view} onClose={close} title="Edit view">
      <div className="space-y-3 p-4">
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Name</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Who can see it</label>
          <Select value={scope} onChange={(e) => setScope(e.target.value as typeof scope)}>
            <option value="private">Just me</option>
            <option value="group">Chosen groups</option>
            <option value="org">Everyone on the service desk</option>
          </Select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Rows per page</label>
          <Select value={pageSize} onChange={(e) => setPageSize(e.target.value)}>
            <option value="">Default (50)</option>
            {PAGE_SIZES.map((n) => (
              <option key={n} value={String(n)}>
                {n}
              </option>
            ))}
          </Select>
          <p className="mt-1 text-xs text-muted-foreground">Also the step “Load more” takes.</p>
        </div>
        {scope === 'group' && (
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              Groups <span className="font-normal">— pick as many as apply</span>
            </label>
            {/* A real multi-select: the whole point of this change is that a
                view can belong to more than one group. */}
            <Select
              multiple
              value={groupIds as unknown as string}
              onChange={(e) => setGroupIds(Array.from(e.target.selectedOptions).map((o) => o.value))}
              className="h-32"
              aria-label="Groups"
            >
              {(groups.data?.entries ?? []).map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </Select>
            {groupIds.length === 0 && (
              // Said before saving rather than discovered afterwards: a
              // group-scoped view with no groups is visible to nobody, which
              // looks exactly like the view having been deleted.
              <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-400">
                With no groups chosen, nobody but you will see this view.
              </p>
            )}
          </div>
        )}
        <p className="text-[11px] text-muted-foreground">
          The filter itself is whatever the queue is showing when you save it — so what you looked at is what
          you saved.
        </p>
        {error && <p className="text-xs text-destructive">{error}</p>}
        <div className="flex justify-end gap-2 border-t pt-3">
          <Button variant="outline" onClick={close}>
            Cancel
          </Button>
          <Button disabled={!dirty || !name.trim() || save.isPending} onClick={() => save.mutate()}>
            Save
          </Button>
        </div>
      </div>
    </Modal>
  );
}
